import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { showError } from '../utils/showError';
import {
  PageHeader,
  Card,
  FormField,
  FormGroup,
  Button,
  Badge,
  FormCheckbox
} from '../components/common';

const CreateUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    roles: []
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAvailableRoles();
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      const response = await api.get('/Users/roles');
      setAvailableRoles(response.data || []);
    } catch (err) {
      console.error('Failed to fetch available roles:', err);
      setAvailableRoles([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userName.trim()) {
      newErrors.userName = 'Username is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      const userData = {
        userName: formData.userName,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        roles: formData.roles
      };

      const response = await api.post('/Users', userData);
      navigate(`/users/${response.data.userId}`);
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleVariant = (role) => {
    const variants = {
      'Admin': 'danger',
      'SuperAdmin': 'secondary',
      'Manager': 'primary',
      'Owner': 'accent',
      'Resident': 'secondary',
      'Staff': 'outline',
      'Support': 'secondary'
    };
    return variants[role] || 'secondary';
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Create New User"
        backLink={{ label: "Back to Users", href: "/users" }}
      />

      <div className="max-w-4xl">

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormGroup title="Basic Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Username"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  error={errors.userName}
                  required
                />

                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  required
                />

                <FormField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={errors.firstName}
                  required
                />

                <FormField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={errors.lastName}
                  required
                />

                <FormField
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />

                <FormField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={errors.password}
                  helpText="Password must be at least 6 characters"
                  required
                />

                <FormField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={errors.confirmPassword}
                  required
                />
              </div>
            </FormGroup>

            <FormGroup title="User Roles" description="Select the roles for this user">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableRoles.map((role) => (
                  <div key={role} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <FormCheckbox
                        name={`role-${role}`}
                        checked={formData.roles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                      />
                      <span className="font-medium text-gray-900">{role}</span>
                    </div>
                    <Badge variant={getRoleVariant(role)} size="small">
                      {role}
                    </Badge>
                  </div>
                ))}
              </div>
            </FormGroup>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CreateUser;